import { useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { useAssignRef } from "shared/lib/useAssignRef"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { Action } from "shared/types/Action"
import { FormRef } from "shared/types/ReactHookForm"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  UseFloorActionsArgs,
  useFloorActions
} from "features/floor-management/model/useFloorActions"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { FloorFormModel } from "features/floor-management/types/FloorFormModel"

import { ProjectPageFloorSidebarProps } from "../ProjectPageFloorSidebar"
import { ProjectPageFloorSidebarViewModel } from "../ProjectPageFloorSidebarView"

export function useProjectPageFloorSidebarViewModel({
  id,
  floor,
  floorFormModel,
  callbacks,
  isDisabled,
  isPending,
  productTotal,
  price,
  isLoadingPrice,
  hideControls,
  isEditing,
  enterEditing,
  exitEditing,
  sidebarActiveTab,
  setSidebarActiveTab
}: ProjectPageFloorSidebarProps): ProjectPageFloorSidebarViewModel {
  const { t } = useTranslation()

  const formRef: FormRef<FloorFormData> = useRef(null)
  useAssignRef({ ref: floorFormModel.formRef, value: formRef.current })

  const submit = useEvent((formData: FloorFormData) =>
    floorFormModel.onSubmit?.(formData)
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<FloorFormData>({
    onSubmit: submit,
    onSuccess: exitEditing
  })

  const isControlsDisabled = isPending || isDisabled

  const actionsArgs: UseFloorActionsArgs = {
    ...callbacks,
    idPrefix: "project-sidebar",
    onEdit: enterEditing,
    isEditDisabled: isControlsDisabled,
    isDuplicateDisabled: isControlsDisabled,
    isCopyDisabled: false,
    onAssignAttachments: undefined,
    onAddClarificationRequest: undefined,
    isDeleteDisabled: isControlsDisabled
  }

  const { actions: normalActions } = useFloorActions(actionsArgs)

  const editingActions = useMemo<Action[]>(
    () => [
      {
        id: "floor__edit__cancel",
        "data-testid": "floor__edit__cancel",
        onClick: exitEditing,
        icon: IconName.CLOSE_CIRCLE,
        label: t("General.Cancel")
      },
      {
        id: "floor__edit__submit",
        "data-testid": "floor__edit__submit",
        type: "submit",
        form: id,
        icon: IconName.CHECK_CIRCLE,
        label: t("General.Confirm"),
        disabled: isSubmitting
      }
    ],
    [exitEditing, id, isSubmitting, t]
  )

  const derivedFormModel = useMemo<FloorFormModel>(
    () => ({
      ...floorFormModel,
      onSubmit: handleSubmit,
      formRef
    }),
    [floorFormModel, handleSubmit, formRef]
  )

  return {
    id,
    actions: isEditing ? editingActions : normalActions,
    floorFormModel: derivedFormModel,
    isDisabled: isPending || isSubmitting,
    isReadonly: !isEditing,
    hideControls,
    price,
    productTotal,
    isLoadingPrice,
    floor,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
